import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Play, Scale, MessageSquare, Users, Shield, Zap, TrendingUp, BarChart3, ChevronRight, Flame } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';
import { Footer, DebateSandbox, TrustBadges } from '../components/marketing';

const MORPHING_TEXTS = [
  'in your boardroom',
  'in your thesis defense',
  'in your cap table',
  'in your portfolio',
  'in your architecture review',
  'in your campaign strategy',
];

const SEGMENTS = [
  { label: 'Startup Founder', value: 'startup', icon: Zap },
  { label: 'Enterprise Leader', value: 'enterprise', icon: Shield },
  { label: 'Consultant', value: 'consultant', icon: BarChart3 },
  { label: 'Engineer', value: 'engineering', icon: Scale },
  { label: 'Academic', value: 'academic', icon: MessageSquare },
  { label: 'Investor', value: 'finance', icon: TrendingUp },
];

const DECISION_LIBRARY = [
  {
    title: 'Monolith vs Microservices',
    segment: 'Engineering',
    verdict: 'Phased migration recommended',
    consensus: '82%',
    voices: 5,
    rounds: 3,
  },
  {
    title: 'Series A at $40M Valuation',
    segment: 'Finance',
    verdict: 'Conditional invest — due diligence gaps identified',
    consensus: '76%',
    voices: 5,
    rounds: 4,
  },
  {
    title: 'Pivot to Enterprise or Stay SMB',
    segment: 'Startup',
    verdict: 'Dual-track recommended with enterprise beachhead',
    consensus: '88%',
    voices: 5,
    rounds: 3,
  },
];

const SOLUTIONS = [
  { title: 'Startups', desc: 'Board of advisors you can\'t afford', icon: Zap, color: '#FF4D00' },
  { title: 'Enterprise', desc: 'Auditable AI governance', icon: Shield, color: '#00D4FF' },
  { title: 'Consulting', desc: 'Better recommendations, faster', icon: BarChart3, color: '#F2A900' },
  { title: 'Engineering', desc: 'Architecture reviews', icon: Scale, color: '#FF4D00' },
  { title: 'Academia', desc: 'Thesis defense simulation', icon: MessageSquare, color: '#7C3AED' },
  { title: 'Finance', desc: 'Investment committee', icon: TrendingUp, color: '#22C55E' },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [morphIndex, setMorphIndex] = useState(0);
  const [selectedSegment, setSelectedSegment] = useState('startup');

  // Morphing text rotation
  useEffect(() => {
    const timer = setInterval(() => {
      setMorphIndex(i => (i + 1) % MORPHING_TEXTS.length);
    }, 2500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />

      {/* Hero — 85vh min */}
      <section className="min-h-[85vh] flex items-center pt-20 pb-12 px-4 md:pt-28 md:pb-20 md:px-6" data-testid="hero-section">
        <div className="max-w-5xl mx-auto w-full">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-[rgba(245,241,232,0.08)] text-[rgba(245,241,232,0.48)] text-xs font-mono mb-6 md:mb-8 tracking-wider">
              <div className="w-2 h-2 bg-[#FF4D00] animate-thermal-pulse" />
              5 AI VOICES. GENUINE DISAGREEMENT. DECISION INSURANCE.
            </div>

            <h1 className="text-display-lg sm:text-display-xl md:text-[5.5rem] md:leading-[0.95] font-display text-[#F5F1E8] uppercase mb-6" data-testid="hero-headline">
              Every Decision<br />
              Deserves a<br />
              <span className="text-[#FF4D00]">Dissenting Voice</span>
            </h1>

            {/* Morphing subhead */}
            <div className="h-8 mb-8 overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.p
                  key={morphIndex}
                  initial={{ opacity: 0, filter: 'blur(8px)', y: 8 }}
                  animate={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
                  exit={{ opacity: 0, filter: 'blur(8px)', y: -8 }}
                  transition={{ duration: 0.4 }}
                  className="text-lg text-[rgba(245,241,232,0.56)] font-body italic"
                >
                  ...{MORPHING_TEXTS[morphIndex]}
                </motion.p>
              </AnimatePresence>
            </div>

            {/* Segment Selector */}
            <div className="flex flex-wrap items-center gap-2 mb-8">
              <span className="text-xs font-mono text-[rgba(245,241,232,0.48)] tracking-wider mr-1">I'M A</span>
              {SEGMENTS.map(seg => (
                <button
                  key={seg.value}
                  onClick={() => setSelectedSegment(seg.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono tracking-wider border transition-all ${
                    selectedSegment === seg.value
                      ? 'border-[#FF4D00] text-[#FF4D00] bg-[#FF4D00]/5'
                      : 'border-[rgba(245,241,232,0.08)] text-[rgba(245,241,232,0.48)] hover:border-[rgba(245,241,232,0.16)]'
                  }`}
                >
                  <seg.icon className="w-3 h-3" />
                  {seg.label}
                </button>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <button
                onClick={() => navigate(user ? '/playbooks' : '/signup')}
                className="px-8 py-3 min-h-[48px] bg-[#FF4D00] text-[#0A0A0A] font-display text-sm font-bold uppercase tracking-wider hover:bg-[#FF6B33] active:bg-[#CC3D00] transition-all flex items-center justify-center gap-2 group"
                data-testid="cta-start-fray"
              >
                Start Debating
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <Link
                to="/solutions"
                className="px-8 py-3 min-h-[48px] border border-[rgba(245,241,232,0.08)] text-[#F5F1E8] font-display text-sm font-bold uppercase tracking-wider hover:border-[#FF4D00] transition-all flex items-center justify-center gap-2"
                data-testid="cta-see-solutions"
              >
                <Play className="w-4 h-4" />
                See How It Works
              </Link>
              <Link
                to="/try"
                className="px-8 py-3 min-h-[48px] border border-[#FF4D00]/30 text-[#FF4D00] font-display text-sm font-bold uppercase tracking-wider hover:bg-[#FF4D00]/10 transition-all flex items-center justify-center gap-2"
                data-testid="cta-try-fotd"
              >
                <Flame className="w-4 h-4" />
                Try Today's Fray
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* DebateSandbox */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]" data-testid="sandbox-section">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="text-xs font-mono text-[#FF4D00]">01</span>
              <div className="w-8 h-px bg-[#FF4D00]/40" />
              <h2 className="text-display-sm font-display text-[#F5F1E8] uppercase">Try It Now</h2>
            </div>
            <p className="text-sm text-[rgba(245,241,232,0.72)] mb-6 font-body">
              No signup required. Run a debate and see five AI voices genuinely disagree about your decision.
            </p>
            <DebateSandbox segment={selectedSegment} />
          </motion.div>
        </div>
      </section>

      {/* Decision Library Highlights */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]" data-testid="decision-library">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <span className="text-xs font-mono text-[#FF4D00]">02</span>
                <div className="w-8 h-px bg-[#FF4D00]/40" />
                <h2 className="text-display-sm font-display text-[#F5F1E8] uppercase">Decision Library</h2>
              </div>
              <Link to="/gallery" className="text-xs font-mono text-[#FF4D00] hover:text-[#FF6B33] flex items-center gap-1 transition-colors">
                VIEW ALL <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-4">
            {DECISION_LIBRARY.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="group bg-[#1E1E1E] border border-[rgba(245,241,232,0.08)] hover:border-[rgba(245,241,232,0.16)] transition-all p-5"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] font-mono text-[#00D4FF] tracking-wider uppercase">{item.segment}</span>
                  <span className="text-[10px] font-mono text-[rgba(245,241,232,0.32)]">
                    {item.voices} voices · {item.rounds} rounds
                  </span>
                </div>
                <h3 className="text-sm font-display font-bold text-[#F5F1E8] uppercase tracking-tight mb-2 group-hover:text-[#FF4D00] transition-colors">
                  {item.title}
                </h3>
                <p className="text-xs text-[rgba(245,241,232,0.64)] leading-relaxed mb-3 font-body">
                  {item.verdict}
                </p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1 bg-[#0A0A0A] overflow-hidden">
                    <div
                      className="h-full bg-[#22C55E]"
                      style={{ width: item.consensus }}
                    />
                  </div>
                  <span className="text-[10px] font-mono text-[#22C55E]">{item.consensus}</span>
                </div>
                {/* Provenance badge */}
                <div className="mt-3 pt-3 border-t border-[rgba(245,241,232,0.04)]">
                  <span className="text-[9px] font-mono text-[rgba(245,241,232,0.32)] tracking-wider">
                    DEBATED BY 5-MODEL FRAY COMMITTEE
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]" data-testid="solutions-grid">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-8">
              <span className="text-xs font-mono text-[#FF4D00]">03</span>
              <div className="w-8 h-px bg-[#FF4D00]/40" />
              <h2 className="text-display-sm font-display text-[#F5F1E8] uppercase">Solutions for Every Decision Maker</h2>
            </div>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {SOLUTIONS.map((sol, i) => (
              <motion.div
                key={sol.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
              >
                <Link
                  to="/solutions"
                  className="block p-4 bg-[#1E1E1E]/50 border border-[rgba(245,241,232,0.08)] hover:border-[rgba(245,241,232,0.16)] transition-all group"
                >
                  <sol.icon className="w-4 h-4 mb-2" style={{ color: sol.color }} />
                  <h3 className="text-xs font-display font-bold text-[#F5F1E8] uppercase tracking-tight mb-0.5 group-hover:text-[#FF4D00] transition-colors">
                    {sol.title}
                  </h3>
                  <p className="text-[10px] text-[rgba(245,241,232,0.48)] font-mono">{sol.desc}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]" data-testid="trust-signals">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: '10+', label: 'AI Models Available' },
              { value: '5', label: 'Voices Per Debate' },
              { value: '8', label: 'Industry Segments' },
              { value: '<$2K', label: 'Monthly Burn Rate' },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <span className="text-display-md font-display text-[#FF4D00]">{stat.value}</span>
                <p className="text-[10px] font-mono text-[rgba(245,241,232,0.48)] tracking-wider uppercase mt-1">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
